if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }
      // Optional thumbnail slider nav in modal
      connectedCallback() {
        super.connectedCallback();
        if (this.thumbnailsInitialized) return;
        this.thumbnails = this.querySelector('.product-media-modal__thumbnails');
        this.content = this.querySelector('.product-media-modal__content');
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        if (!this.thumbnails || !this.content) return;
        this.thumbnailsInitialized = true;

        this.thumbnails.addEventListener('pointerup', (event) => event.stopPropagation());
        this.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => this.scrollToMedia(thumbnail.dataset.target));
        });

        [this.prevButton, this.nextButton].forEach((button) => {
          if (!button) return;
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });
        if (this.prevButton) this.prevButton.addEventListener('click', () => this.stepToMedia(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.stepToMedia(1));

        this.content.querySelectorAll('.product-media-modal__zoomable').forEach((image) => {
          image.addEventListener('pointerdown', (event) => this.handleZoomPointerDown(image, event));
        });

        this.content.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => this.syncActiveThumbnailToScroll(), 100);
          },
          { passive: true }
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }
      
      // Optional thumbnail slider nav in modal
      scrollToMedia(mediaId) {
        const target = this.content.querySelector(`[data-media-id="${mediaId}"]`);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }

      // Optional prev/next arrows in modal - step one slide at a time
      stepToMedia(direction) {
        const slides = Array.from(this.content.querySelectorAll('[data-media-id]'));
        if (!slides.length) return;

        const activeThumbnail = this.thumbnails?.querySelector('[aria-current="true"]');
        const activeId = activeThumbnail?.dataset.target;
        let currentIndex = slides.findIndex((slide) => slide.dataset.mediaId === activeId);
        if (currentIndex === -1) currentIndex = 0;

        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
        const nextSlide = slides[nextIndex];
        if (nextSlide) this.scrollToMedia(nextSlide.dataset.mediaId);
      }

      // Optional click-to-zoom in modal - click and hold to pan while zoomed (desktop only;
      // mobile can tap to zoom but not drag-pan, since dragging is reserved for swiping slides)
      handleZoomPointerDown(image, event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        if (event.pointerType !== 'mouse') {
          this.zoomDragMoved = false;
          this.zoomStartX = event.clientX;
          this.zoomStartY = event.clientY;

          const onPointerUp = (upEvent) => {
            image.removeEventListener('pointermove', onTouchPointerMove);
            image.removeEventListener('pointerup', onPointerUp);
            if (this.zoomDragMoved) return;
            this.toggleZoom(image, upEvent);
          };

          const onTouchPointerMove = (moveEvent) => {
            const dx = moveEvent.clientX - this.zoomStartX;
            const dy = moveEvent.clientY - this.zoomStartY;
            if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.zoomDragMoved = true;
          };

          image.addEventListener('pointermove', onTouchPointerMove);
          image.addEventListener('pointerup', onPointerUp);
          return;
        }

        event.stopPropagation();
        event.preventDefault();
        image.setPointerCapture(event.pointerId);
        this.zoomDragMoved = false;
        this.zoomStartX = event.clientX;
        this.zoomStartY = event.clientY;

        const onPointerMove = (moveEvent) => {
          const dx = moveEvent.clientX - this.zoomStartX;
          const dy = moveEvent.clientY - this.zoomStartY;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.zoomDragMoved = true;
          if (image.classList.contains('is-zoomed')) this.updateZoomOrigin(image, moveEvent);
        };

        const onPointerUp = (upEvent) => {
          upEvent.stopPropagation();
          image.releasePointerCapture(upEvent.pointerId);
          image.removeEventListener('pointermove', onPointerMove);
          image.removeEventListener('pointerup', onPointerUp);
          if (this.zoomDragMoved && image.classList.contains('is-zoomed')) return;
          this.toggleZoom(image, upEvent);
        };

        image.addEventListener('pointermove', onPointerMove);
        image.addEventListener('pointerup', onPointerUp);
      }

      toggleZoom(image, event) {
        if (image.classList.contains('is-zoomed')) {
          this.resetZoom(image);
          return;
        }
        this.updateZoomOrigin(image, event);
        image.style.transform = 'scale(2.5)';
        image.classList.add('is-zoomed');
      }

      updateZoomOrigin(image, event) {
        const rect = image.getBoundingClientRect();
        const xPercent = Math.min(Math.max(((event.clientX - rect.left) / rect.width) * 100, 0), 100);
        const yPercent = Math.min(Math.max(((event.clientY - rect.top) / rect.height) * 100, 0), 100);
        image.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      }

      resetZoom(image) {
        image.style.transform = '';
        image.style.transformOrigin = '';
        image.classList.remove('is-zoomed');
      }

      syncActiveThumbnailToScroll() {
        const slides = Array.from(this.content.children);
        if (!slides.length) return;
        const center = this.content.scrollLeft + this.content.clientWidth / 2;
        const closest = slides.reduce((closest, slide) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - center);
          return distance < closest.distance ? { slide, distance } : closest;
        }, { slide: null, distance: Infinity }).slide;

        if (closest) this.setActiveThumbnail(closest.dataset.mediaId);
      }

      setActiveThumbnail(mediaId) {
        if (!this.thumbnails) return;
        if (this.activeMediaId && this.activeMediaId !== mediaId) {
          this.content.querySelectorAll('.product-media-modal__zoomable.is-zoomed').forEach((image) => {
            this.resetZoom(image);
          });
        }
        this.activeMediaId = mediaId;
        this.content.querySelectorAll('video').forEach(video => {
  video.pause();
});

const activeSlide = Array.from(this.content.children)
  .find(slide => slide.dataset.mediaId === mediaId);

if (activeSlide?.classList.contains('product-modal-video')) {
  activeSlide.loadContent(false);
}
        this.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          thumbnail.removeAttribute('aria-current');
        });
        const activeThumbnail = this.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        activeThumbnail.setAttribute('aria-current', 'true');
        activeThumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        this.updateNavButtons(mediaId);
      }

      // Optional prev/next arrows in modal - disable at the first/last slide
      updateNavButtons(mediaId) {
        if (!this.prevButton && !this.nextButton) return;
        const slides = Array.from(this.content.querySelectorAll('[data-media-id]'));
        const currentIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (this.prevButton) this.prevButton.disabled = currentIndex <= 0;
        if (this.nextButton) this.nextButton.disabled = currentIndex === -1 || currentIndex >= slides.length - 1;
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        this.setActiveThumbnail(this.openedBy.getAttribute('data-media-id'));

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
